/**
 * DispositionPicker — shown during ACW (After Call Work) after a call ends.
 *
 * Fetches active dispositions from /agent/dispositions, lets the agent
 * select one, and submits it to /calls/:callId/disposition. The parent
 * component controls visibility by passing a callId (null = hidden).
 */
import { useState, useEffect, type JSX } from 'react'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { api } from '../../api/client.js'
import { cn } from '../../utils/cn.js'

interface DispositionOption {
  id: string
  name: string
  description: string | null
}

export interface DispositionPickerProps {
  callId: string | null
  tenantSlug: string
  onSubmitted?: (disposition: string) => void
}

export function DispositionPicker({ callId, onSubmitted }: DispositionPickerProps): JSX.Element | null {
  const [dispositions, setDispositions] = useState<DispositionOption[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!callId) return
    setLoading(true)
    setSubmitted(false)
    setSelected(null)
    api
      .get<{ items: DispositionOption[] }>('/agent/dispositions')
      .then((res) => setDispositions(res.items ?? []))
      .catch(() => setDispositions([]))
      .finally(() => setLoading(false))
  }, [callId])

  if (!callId) return null

  const handleSubmit = async () => {
    if (!selected || !callId) return
    setSubmitting(true)
    try {
      await api.post(`/calls/${callId}/disposition`, { dispositionId: selected })
      setSubmitted(true)
      const disp = dispositions.find((d) => d.id === selected)
      if (disp && onSubmitted) onSubmitted(disp.name)
    } catch {
      // Error is surfaced by the API client's interceptor
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading dispositions...
      </div>
    )
  }

  if (dispositions.length === 0) {
    // No dispositions configured — don't block the agent
    return null
  }

  if (submitted) {
    return (
      <div className="flex items-center gap-2 p-3 text-sm text-green-600">
        <CheckCircle2 className="h-4 w-4" />
        Disposition saved
      </div>
    )
  }

  return (
    <div className="border-t border-border p-3 space-y-2">
      <div className="text-sm font-medium text-foreground">
        Select a disposition to complete wrap-up
      </div>
      <div className="flex flex-wrap gap-1.5">
        {dispositions.map((d) => (
          <button
            key={d.id}
            onClick={() => setSelected(d.id)}
            className={cn(
              'px-2.5 py-1 text-xs rounded-[3px] border transition-colors',
              selected === d.id
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background hover:bg-accent',
            )}
            title={d.description ?? undefined}
          >
            {d.name}
          </button>
        ))}
      </div>
      <button
        onClick={handleSubmit}
        disabled={!selected || submitting}
        className={cn(
          'w-full px-3 py-1.5 text-sm rounded-[3px] font-medium transition-colors',
          selected && !submitting
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-muted-foreground cursor-not-allowed',
        )}
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving...
          </span>
        ) : (
          'Save Disposition'
        )}
      </button>
    </div>
  )
}
